import type { APIService } from '../interfaces/services';
import { fileExtension, postFileToPresignedUrl, type PresignedUploadResponse } from './AttachmentService';

export type HubDocumentStatus = 'processing' | 'ready' | 'failed';

export type HubDocument = {
  object: 'assistant_document';
  id: string;
  file_name: string;
  status: HubDocumentStatus;
  error_code: string;
  retrieval_enabled: boolean;
  created_at: string;
};

type HubDocumentList = {
  object: 'list';
  total_count: number;
  data: HubDocument[];
};

export type DocumentHubApi = {
  listDocuments: () => Promise<HubDocument[]>;
  uploadDocument: (file: File, onProgress: (pct: number) => void, signal: AbortSignal) => Promise<HubDocument>;
  deleteDocument: (id: string) => Promise<void>;
  setDocumentEnabled: (id: string, enabled: boolean) => Promise<HubDocument>;
};

const DOCUMENTS_URL = '/api/v1/assistant/documents/';

export function createDocumentHubApi(apiService: APIService): DocumentHubApi {
  return {
    listDocuments: async () => (await apiService.get<HubDocumentList>(DOCUMENTS_URL)).data,
    uploadDocument: async (file, onProgress, signal) => {
      const { upload_uuid, presigned_post } = await apiService.post<PresignedUploadResponse>(DOCUMENTS_URL, {
        file_name: file.name,
        extension: fileExtension(file),
      });
      try {
        await postFileToPresignedUrl(file, presigned_post, onProgress, signal);
      } catch (error) {
        await apiService.post(`${DOCUMENTS_URL}${upload_uuid}/delete/`, {}).catch(() => undefined);
        throw error;
      }
      return apiService.post<HubDocument>(`${DOCUMENTS_URL}${upload_uuid}/index/`, {});
    },
    deleteDocument: async (id) => {
      await apiService.post(`${DOCUMENTS_URL}${id}/delete/`, {});
    },
    setDocumentEnabled: async (id, enabled) =>
      apiService.post<HubDocument>(`${DOCUMENTS_URL}${id}/${enabled ? 'enable' : 'disable'}/`, {}),
  };
}
